/**
 * Zugriff auf die API.
 *
 * Die Anmeldung läuft über ein HttpOnly-Cookie, das der Browser von selbst
 * mitschickt -- hier gibt es deshalb keinen Token zu verwalten. `credentials`
 * muss trotzdem gesetzt sein, sonst lässt fetch das Cookie weg, sobald
 * Oberfläche und Backend im Entwicklungsbetrieb auf verschiedenen Ports
 * liegen.
 */

import { WORTLAUT } from '../texte'
import { maintenanceFrom, maintenanceSeen } from '../lib/maintenance'

export class ApiError extends Error {
  readonly status: number
  /** The caller's time limit ran out before the server answered (0.30.9). */
  readonly timedOut: boolean
  /**
   * The server is in maintenance (#131): a 503 that is no failure and no
   * refusal. The outbox waits on it without counting an attempt.
   */
  readonly maintenance: boolean

  constructor(status: number, message: string, timedOut = false, maintenance = false) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.timedOut = timedOut
    this.maintenance = maintenance
  }

  /** Nicht angemeldet -- die Oberfläche schickt dann zur Anmeldung. */
  get unauthorized(): boolean {
    return this.status === 401
  }

  /**
   * Es lag am Netz, nicht an der Anfrage.
   *
   * Status 0 gibt es in keiner HTTP-Antwort; er steht hier für „gar keine
   * Antwort". Daran hängt eine Entscheidung, die man nicht raten darf: was am
   * Netz scheitert, wartet im Ausgangskorb auf später — was der Server
   * ablehnt, wartet nie, sondern gehört dem Menschen gesagt.
   */
  get offline(): boolean {
    return this.status === 0
  }
}

/**
 * Ob dieser Fehlschlag am Netz lag.
 *
 * Die Frage stellt sich an mehreren Stellen, und überall hängt dieselbe
 * Entscheidung daran: **kein Netz ist kein Ausfall.** Ein Netzfehler darf
 * nichts zumachen, was gerade offen ist -- die Eingabezeile schon gar nicht,
 * denn sie ist der Weg in den Ausgangskorb.
 */
/**
 * Was von einem Fehler dasteht: seine Meldung, sonst der eine Satz.
 *
 * Aus der Codereview (0.29.0): derselbe Dreizeiler stand zehnmal in den
 * Karten. Der Server spricht Deutsch, der Browser auch; hier wird nur
 * entschieden, welches von beiden.
 */
export function fehlertext(fehler: unknown): string {
  // Without a connection the sentence is the one about the connection
  // (1.112.0, review): this three-liner stood at eight places.
  if (ohneNetz(fehler)) return WORTLAUT.allgemein.ohneNetz
  return fehler instanceof Error ? fehler.message : WORTLAUT.allgemein.schiefgelaufen
}

export function ohneNetz(fehler: unknown): boolean {
  return fehler instanceof ApiError && fehler.offline
}

/** Whether this failure was the maintenance (#131) -- waiting, like no net. */
export function inMaintenance(error: unknown): boolean {
  return error instanceof ApiError && error.maintenance
}

/**
 * Ob gerade etwas durchgeht -- gemessen, nicht geglaubt.
 *
 * `navigator.onLine` ist eine freundliche Vermutung: es sagt „online", sobald
 * irgendeine Verbindung besteht. Ein WLAN, das keine Verbindung nach draußen
 * hat, ein Mobilfunknetz mit einem Balken, ein Hotspot mit Anmeldeseite --
 * alles „online", und trotzdem kommt nichts an. Umgekehrt meldet iOS den
 * Wechsel manchmal spät.
 *
 * Deshalb zählt hier, **was tatsächlich passiert ist**: jede geglückte
 * Anfrage stellt auf „Netz", jeder Netzfehler auf „kein Netz". Das ist die
 * einzige Auskunft, die nicht raten muss.
 */
let netzDa = true
const netzHoerer = new Set<() => void>()

export function netzStand(): boolean {
  // Was der Browser weiß, gilt trotzdem: schaltet jemand den Flugmodus ein,
  // steht es sofort fest und nicht erst beim nächsten Fehlschlag.
  return netzDa && navigator.onLine
}

/** The app's own mark alone -- without the browser's flag, which iOS in the home-screen app does not keep (1.50.1). */
export function netzMarke(): boolean {
  return netzDa
}

export function netzBeobachten(bei: () => void): () => void {
  netzHoerer.add(bei)
  return () => netzHoerer.delete(bei)
}

/**
 * Die Marke wieder auf „vielleicht geht es" stellen.
 *
 * **Weil ein Fehlschlag sonst seine eigene Wiederholung verhindert** (1.39.0).
 * Aus dem Betrieb: „es funktioniert, aber nicht automatisch und nicht beim
 * ersten Versuch, und offenbar nur nach einem Neustart der App."
 *
 * Genau so war es gebaut. `netzDa` fängt beim Start auf `true` an; ein
 * abgebrochener Upload setzt es über `xhr.onerror` auf `false`, und
 * `senden()` im Ausgangskorb beginnt mit „kein Netz, dann gar nicht erst".
 * Danach hing alles daran, dass **irgendeine andere** Anfrage durchkam und
 * die Marke zurücksetzte. Ein Neustart tat das zuverlässig -- sonst nichts.
 *
 * Diese Funktion tut, was ein Neustart tut, und sie gehört an die
 * Gelegenheiten, bei denen sich die Lage geändert haben kann: die App kommt
 * nach vorn, der Browser meldet `online`. **Die Anfrage danach entscheidet**,
 * nicht diese Zeile; sie nimmt nur eine alte Auskunft zurück, statt sie für
 * eine Tatsache zu halten.
 *
 * `navigator.onLine` bleibt unangetastet: wer im Flugmodus steht, bekommt
 * durch eine optimistische Marke kein Netz (`netzStand()` fragt beides ab).
 */
export function netzNeuBewerten(): void {
  netzMelden(true)
}

/**
 * Called on **every** answer from the server, not only when the mark flips
 * (1.50.1). `netzBeobachten` fires once, when the net comes back as the app
 * sees it; on an iPad in the home-screen app neither that nor the browser's
 * `online` reached the pictures of the trip list after a start without net
 * (from use: "Symbolbilder werden nicht geladen nach Netzwechsel"), while
 * the posts did arrive -- so an answer did. This is that answer as a sign.
 */
const antwortHoerer = new Set<() => void>()

export function antwortBeobachten(bei: () => void): () => void {
  antwortHoerer.add(bei)
  return () => antwortHoerer.delete(bei)
}

/**
 * An answer came -- from `request()` or from an upload, and from nowhere
 * else. Not from `netzNeuBewerten()`: that only takes an old verdict back
 * and is called when the app comes to the front, which is no answer. The
 * first cut had the sign inside `netzMelden(true)` and retried pictures
 * while still offline.
 */
function antwortMelden(): void {
  for (const h of antwortHoerer) h()
}

function netzMelden(neu: boolean): void {
  if (netzDa === neu) return
  netzDa = neu
  for (const h of netzHoerer) h()
}

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '')

/** `AbortSignal.timeout()` where it exists (iOS 16); otherwise by hand. */
export function zeitgrenze(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === 'function') return AbortSignal.timeout(ms)
  const controller = new AbortController()
  setTimeout(() => controller.abort(), ms)
  return controller.signal
}

/*
 * **Every request ends, also the one nobody gave a limit** (1.78.0).
 *
 * Until here a request without a caller's signal had no end but the one
 * WebKit picks, and on a dead mobile link or right before the app is sent to
 * sleep that can be a very long time. It was not one request that hung, it
 * was everything behind it: react-query hangs every further poll on the
 * running one, so the chat and the chat list stopped updating, also after
 * leaving and coming back; the send button stayed locked (`isPending`) and
 * the text did not go to the outbox; and the outbox itself stayed on
 * `laeuft` and sent nothing until the app was restarted.
 *
 * A read gets half a minute -- a poll answers in well under a second. A
 * write gets longer, because the server may be working (a post rings every
 * reader's phone before it answers). Whatever takes longer on purpose
 * passes its own limit (the book, the backup, the numbers). Running out
 * counts as no net: the outbox tries again later, and the key of a post
 * keeps that from becoming a second one.
 */
const LESEN_MS = 30_000
const SCHREIBEN_MS = 90_000
/**
 * An upload is not limited in its length -- a video on one bar takes what it
 * takes -- but in its **standstill**: no progress and no answer for this
 * long, and it is given up. A minute, because after the last byte the
 * server still works before it answers (a recording is parsed, a photo's
 * data read), and that gives no sign of life either.
 */
const STILLSTAND_MS = 60_000

async function request<T>(
  path: string,
  init: RequestInit & { query?: Record<string, string | number | undefined> } = {},
): Promise<T> {
  const { query, ...rest } = init
  let url = `${BASE}${path}`
  if (query) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) params.set(key, String(value))
    }
    const qs = params.toString()
    if (qs) url += `?${qs}`
  }

  // Ohne Netz wirft `fetch` einen TypeError, dessen Text vom Browser kommt
  // („Load failed" in Safari, „Failed to fetch" in Chrome). Der stünde sonst
  // wörtlich in der Oberfläche -- und ließe sich nirgends von einer Absage des
  // Servers unterscheiden.
  const eigeneGrenze = rest.signal === undefined || rest.signal === null
  const signal = eigeneGrenze
    ? zeitgrenze((rest.method ?? 'GET') === 'GET' ? LESEN_MS : SCHREIBEN_MS)
    : rest.signal
  let response: Response
  try {
    response = await fetch(url, {
      credentials: 'include',
      ...rest,
      signal,
      headers: {
        ...(rest.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(rest.headers as Record<string, string> | undefined),
      },
    })
  } catch (error) {
    // Our own limit ran out: that is the net, as said at `LESEN_MS`.
    if (eigeneGrenze && signal?.aborted === true) {
      netzMelden(false)
      throw new ApiError(0, WORTLAUT.technik.fehler.keineVerbindung)
    }
    // A time limit of the caller (`signal`) is not the net: the server may be
    // working still -- from use, a book that said "wird gebaut" for minutes
    // without an end (0.30.9). It is named as what it is.
    if (rest.signal?.aborted === true || (error instanceof DOMException && (error.name === 'AbortError' || error.name === 'TimeoutError'))) {
      throw new ApiError(0, WORTLAUT.technik.fehler.keineAntwort, true)
    }
    netzMelden(false)
    throw new ApiError(0, WORTLAUT.technik.fehler.keineVerbindung)
  }

  // Es kam etwas zurück -- egal mit welchem Status. Auch ein 404 ist ein
  // Beweis dafür, dass die Leitung steht.
  netzMelden(true)
  antwortMelden()

  if (!response.ok) {
    throw errorFrom(await response.text().catch(() => ''), response.status)
  }
  if (response.status === 204) return undefined as T
  /*
   * **The body can fail after the head arrived** (1.78.0): the connection
   * drops halfway, or a hotspot answers with its own page. Unwrapped, the
   * browser's raw text stood in the interface ("Load failed"), and the
   * outbox took it for a refusal of the server and never tried again --
   * although the server mostly had the post already. It is the net, and it
   * is said as the net; the post's key keeps the next attempt from making
   * a second one.
   */
  try {
    return (await response.json()) as T
  } catch {
    netzMelden(false)
    throw new ApiError(0, WORTLAUT.technik.fehler.keineVerbindung)
  }
}

/**
 * The error of an answer that is not ok -- and the one place where a
 * maintenance is noticed (#131): `fetch` and the upload both come here, and
 * `lib/maintenance.ts` hears it once for the whole app.
 */
function errorFrom(body: string, status: number): ApiError {
  if (status === 503) {
    let data: unknown = null
    try {
      data = JSON.parse(body)
    } catch {
      // No JSON: the host itself, not our maintenance.
    }
    const maintenance = maintenanceFrom(data)
    if (maintenance !== null) {
      maintenanceSeen(maintenance)
      return new ApiError(status, errorText(body, status), false, true)
    }
  }
  return new ApiError(status, errorText(body, status))
}

function errorText(body: string, status: number): string {
  try {
    const data = JSON.parse(body)
    const detail = (data as { detail?: unknown }).detail
    if (typeof detail === 'string') return detail
    // Validierungsfehler von FastAPI kommen als Liste; die erste Meldung
    // reicht, mehr als eine hilft dem Nutzer ohnehin nicht weiter.
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string }
      if (first?.msg) return first.msg
    }
  } catch {
    // Antwort ohne JSON -- dann bleibt es beim allgemeinen Text unten.
  }
  if (status >= 500) return WORTLAUT.technik.fehler.serverfehler
  return WORTLAUT.allgemein.schiefgelaufen
}

/**
 * Ein Bild hochladen und dabei sagen, wie weit es ist.
 *
 * Hier steht als einzige Stelle der Anwendung `XMLHttpRequest` statt `fetch`:
 * `fetch` meldet keinen Fortschritt beim Senden. Ein Rumpf als Datenstrom
 * wäre der moderne Weg, aber Safari kennt ihn nicht -- und Safari ist auf
 * dieser Reise das Hauptgerät. Unterwegs am schmalen Netz ist der Balken
 * nicht Zierde: ohne ihn sieht ein Upload von zwanzig Sekunden aus wie ein
 * Absturz.
 */
function uploadRequest<T>(
  path: string,
  blob: Blob,
  onProgress?: (anteil: number) => void,
  method: 'POST' | 'PATCH' = 'POST',
  headers: Record<string, string> = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, `${BASE}${path}`)
    xhr.withCredentials = true
    xhr.setRequestHeader('Content-Type', blob.type || 'application/octet-stream')
    for (const [name, wert] of Object.entries(headers)) {
      xhr.setRequestHeader(name, wert)
    }

    // The standstill watch (`STILLSTAND_MS`): every sign of life restarts it.
    let stillstand = false
    let uhr: ReturnType<typeof setTimeout> | undefined
    const wachen = () => {
      clearTimeout(uhr)
      uhr = setTimeout(() => {
        stillstand = true
        xhr.abort()
      }, STILLSTAND_MS)
    }
    xhr.onloadend = () => clearTimeout(uhr)
    xhr.onprogress = wachen

    xhr.upload.onprogress = (event) => {
      wachen()
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total)
    }

    xhr.onload = () => {
      // Auch hier gilt: es kam etwas zurück, also steht die Leitung.
      netzMelden(true)
      antwortMelden()
      if (xhr.status >= 200 && xhr.status < 300) {
        // Der Server hat es; der Balken darf voll sein, auch wenn das
        // letzte Fortschrittsereignis ausgeblieben ist.
        onProgress?.(1)
        try {
          resolve(JSON.parse(xhr.responseText) as T)
        } catch {
          reject(new ApiError(xhr.status, WORTLAUT.technik.fehler.unlesbar))
        }
        return
      }
      reject(errorFrom(xhr.responseText, xhr.status))
    }

    xhr.onerror = () => {
      netzMelden(false)
      reject(new ApiError(0, WORTLAUT.technik.fehler.keineVerbindung))
    }
    xhr.onabort = () => {
      if (stillstand) {
        netzMelden(false)
        reject(new ApiError(0, WORTLAUT.technik.fehler.keineVerbindung))
        return
      }
      reject(new ApiError(0, WORTLAUT.technik.fehler.hochladenAbgebrochen))
    }

    wachen()
    xhr.send(blob)
  })
}

export const api = {
  get: <T>(
    path: string,
    query?: Record<string, string | number | undefined>,
    // A time limit for a page that may take long (the trip's numbers on
    // the first visit after 1.53.2): after it the caller gets
    // `ApiError.timedOut` instead of a card that says "wird geholt" for ever.
    signal?: AbortSignal,
  ) =>
    request<T>(path, { method: 'GET', query, signal }),

  post: <T>(
    path: string,
    body?: unknown,
    query?: Record<string, string | number | undefined>,
    // Fuer Aufnahmeort und -zeit eines Videos: die stehen bei einem Bild als
    // Kopfzeilen an der Datei, und beim Video soll dieselbe Regel gelten --
    // nur dass dort der Rumpf JSON ist und nicht die Datei.
    headers?: Record<string, string>,
    // A time limit for a request that may take long (the book): after it the
    // caller gets `ApiError.timedOut` instead of waiting for ever.
    signal?: AbortSignal,
  ) =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? '{}' : JSON.stringify(body),
      query,
      headers,
      signal,
    }),

  /**
   * Ein Bild -- roh im Rumpf, kein multipart.
   *
   * Der Server erkennt den Typ am Inhalt, nicht am Content-Type; die Angabe
   * hier ist nur Höflichkeit. Ohne multipart-Rahmen ist die Anfrage außerdem
   * so groß wie die Datei und nicht größer, was auf einem Webhosting-Paket
   * mit knappem `post_max_size` zählt.
   */
  upload: <T>(
    path: string,
    blob: Blob,
    onProgress?: (anteil: number) => void,
    headers: Record<string, string> = {},
  ) => uploadRequest<T>(path, blob, onProgress, 'POST', headers),

  /**
   * Ein Stück eines Videos.
   *
   * `Content-Range` sagt, wo das Stück hingehört. Der Server prüft es gegen
   * seinen Stand und lehnt ab, was nicht passt — das ist der Unterschied
   * zwischen „weitermachen" und „Datei kaputtschreiben".
   */
  uploadChunk: <T>(
    path: string,
    blob: Blob,
    range: string,
    onProgress?: (anteil: number) => void,
  ) => uploadRequest<T>(path, blob, onProgress, 'PATCH', { 'Content-Range': range }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body === undefined ? '{}' : JSON.stringify(body),
    }),

  /**
   * `body` ist die Ausnahme und hat einen Grund: das Löschen eines Kontos
   * verlangt die abgetippte E-Mail (#47). Sie als Suchparameter anzuhängen
   * hieße, eine Adresse in Server- und Proxy-Protokolle zu schreiben.
   */
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
}
